import { FOOTER } from "@/constants/testIds";
import { ArrowUpRight, Mail, MessageCircle, MapPin, Clock } from "lucide-react";

const social = [
  { label: "Twitter", href: "https://twitter.com" },
  { label: "GitHub", href: "https://github.com" },
  { label: "LinkedIn", href: "https://linkedin.com" },
  { label: "Instagram", href: "https://instagram.com" },
];

const scrollToContact = () => {
  const el = document.getElementById("contact");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Footer() {
  return (
    <footer
      data-testid={FOOTER.root}
      className="relative pt-24 pb-10 border-t border-white/10 bg-black"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Top CTA row */}
        <div className="mb-16 lg:mb-24 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end pb-16 border-b border-white/10">
          <div className="lg:col-span-8">
            <div className="font-mono-tag text-[10px] text-[#E2FF31] mb-4">
              // Ready when you are
            </div>
            <h3 className="font-display font-medium text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              Let&apos;s design your best{" "}
              <span className="italic font-light text-[#E2FF31]">
                sales asset
              </span>{" "}
              yet.
            </h3>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end flex flex-col gap-3 w-full lg:w-auto">
            <button
              data-testid="footer-cta-book"
              onClick={scrollToContact}
              className="btn-primary justify-center"
            >
              Start a project <span className="arrow">→</span>
            </button>
            <a
              href="https://wa.me/971565674843"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost justify-center"
            >
              WhatsApp me <ArrowUpRight size={14} />
            </a>
          </div>
        </div>

        {/* Massive wordmark */}
        <div
          aria-hidden
          className="font-display font-medium text-[18vw] lg:text-[16vw] leading-[0.85] tracking-tight text-white/[0.08] select-none pointer-events-none mb-16"
        >
          ELEVATE<span className="text-[#E2FF31]/30">.</span>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-14 border-b border-white/10">
          {/* Contact block */}
          <div className="lg:col-span-5 space-y-6">
            <div>
              <div className="font-mono-tag text-[10px] text-white/40 mb-3">
                // Get in touch
              </div>
              <a
                data-testid={FOOTER.email}
                href="https://wa.me/971565674843"
                target="_blank"
                rel="noopener noreferrer"
                className="font-display text-2xl sm:text-3xl font-medium hover:text-[#25D366] transition-colors inline-flex items-center gap-3 group"
              >
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#25D366]/10 border border-[#25D366]/30 group-hover:bg-[#25D366]/20 transition-colors">
                  <MessageCircle size={18} className="text-[#25D366]" />
                </span>
                +971 56 567 4843 <ArrowUpRight size={20} />
              </a>
            </div>
            <button
              data-testid="footer-whatsapp"
              onClick={scrollToContact}
              className="inline-flex items-center gap-3 text-white/80 hover:text-[#E2FF31] transition-colors"
            >
              <Mail size={18} className="text-[#E2FF31]" />
              Use the enquiry form <ArrowUpRight size={14} />
            </button>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-white/50 text-sm">
              <span className="inline-flex items-center gap-2">
                <MapPin size={14} className="text-[#E2FF31]" /> Based in the UAE
              </span>
              <span className="inline-flex items-center gap-2">
                <Clock size={14} className="text-[#E2FF31]" /> Replies within
                24h
              </span>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="font-mono-tag text-[10px] text-white/40 mb-4">
              // Navigate
            </div>
            <ul className="space-y-2 text-white/70">
              {["Home", "Work", "Services", "About", "Contact"].map((l) => (
                <li key={l}>
                  <a
                    href={`#${l.toLowerCase()}`}
                    className="hover:text-[#E2FF31] transition-colors"
                  >
                    {l}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="font-mono-tag text-[10px] text-white/40 mb-4">
              // Elsewhere
            </div>
            <ul className="space-y-2 text-white/70">
              {social.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 hover:text-[#E2FF31] transition-colors"
                  >
                    {s.label} <ArrowUpRight size={14} />
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-4 border-t border-white/10 font-mono-tag text-[10px] text-white/40">
              Studio hours · Mon–Sat · 09:00–19:00 GST
            </div>
          </div>
        </div>

        <div className="pt-8 flex flex-wrap justify-between gap-4 font-mono-tag text-[10px] text-white/40">
          <div>
            © {new Date().getFullYear()} Elevate Studios · All rights
            reserved
          </div>
          <div>Designed & built solo · React · FastAPI · MongoDB</div>
        </div>
      </div>
    </footer>
  );
}
